import { useRef, useEffect, useState, lazy, Suspense } from "react";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { LazyMotion, domAnimation, m } from "framer-motion";
import {
  Ship,
  ArrowRight, Waves, Sparkles, Zap,
  Anchor, BarChart3
} from "lucide-react";
const heroVideo = "/videos/hero-video.mp4";
import yachtGlobal from "@/assets/yacht-global-opt.webp?format=webp&quality=50&w=1200";
import yachtAerial from "@/assets/home-yacht-aerial.jpg?format=webp&quality=45&w=900";
import yachtAerialMobile from "@/assets/home-yacht-aerial.jpg?format=webp&quality=40&w=480";
import yachtBridge from "@/assets/home-yacht-bridge.jpg?format=webp&quality=60&w=1280";
import yachtDeck from "@/assets/home-yacht-deck.jpg?format=webp&quality=60&w=1280";

import { useTranslation } from "react-i18next";

const HomeBelowFold = lazy(() => import("@/components/home/HomeBelowFold"));

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as const } }),
};

const Index = () => {
  const { t } = useTranslation();
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // Native loop attribute handles seamless video looping


  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;

    // Silently handle video load errors (e.g. PageSpeed bot, network issues)
    const handleError = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    video.addEventListener("error", handleError);
    const sources = video.querySelectorAll("source");
    sources.forEach((s) => s.addEventListener("error", handleError));

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.preload = "auto";
          video.load();

          const handleCanPlay = () => {
            setVideoLoaded(true);
            video.play().catch(() => {});
          };

          video.addEventListener("canplay", handleCanPlay, { once: true });
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
      video.removeEventListener("error", handleError);
      sources.forEach((s) => s.removeEventListener("error", handleError));
    };
  }, []);

  const products = [
    {
      icon: Anchor,
      title: t("home.productCharterTitle"),
      desc: t("home.productCharterDesc"),
      modules: t("home.productCharterModules"),
      href: "/yacht-charter-management-software",
      color: "text-teal-glow",
      bg: "bg-teal-glow/10",
      border: "border-teal-glow/20",
    },
    {
      icon: Ship,
      title: t("home.productMgmtTitle"),
      desc: t("home.productMgmtDesc"),
      modules: t("home.productMgmtModules"),
      href: "/yacht-management-software",
      color: "text-primary",
      bg: "bg-primary/10",
      border: "border-primary/20",
    },
    {
      icon: BarChart3,
      title: t("home.productSalesTitle"),
      desc: t("home.productSalesDesc"),
      modules: t("home.productSalesModules"),
      href: "/yacht-brokerage-software",
      color: "text-gold",
      bg: "bg-gold/10",
      border: "border-gold/20",
    },
  ];
  return (
    <Layout>
      
      {/* Hero — DARK */}
      <section className="relative min-h-[80vh] md:min-h-screen flex items-center overflow-hidden section-dark">
        <div className="absolute inset-0">
          {/* Use the same public poster that's preloaded in index.html — already in cache, no extra download */}
          <img
            src="/hero-poster-sm.webp"
            alt={t("alt.heroHome")}
            width={960}
            height={540}
            // @ts-ignore
            fetchpriority="high"
            className={`absolute inset-0 w-full h-full object-cover ${videoLoaded ? 'opacity-0 transition-opacity duration-700' : ''}`}
          />
          <video
            ref={heroVideoRef}
            muted
            playsInline
            aria-hidden="true"
            // @ts-ignore — webkit attribute for older iOS
            webkit-playsinline="true"
            preload="none"
            width={1920}
            height={1080}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ WebkitTransform: "translateZ(0)" }}
            loop
            // @ts-ignore
            fetchpriority="low"
          >
            <source src={heroVideo} type="video/mp4" />
            <track kind="captions" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(220,28%,8%,0.8)] via-[hsl(220,28%,8%,0.7)] to-[hsl(220,28%,8%)] max-md:from-[hsl(220,28%,8%,0.9)] max-md:via-[hsl(220,28%,8%,0.85)] max-md:to-[hsl(220,28%,8%)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(220,28%,8%,0.6)] via-transparent to-[hsl(220,28%,8%,0.6)]" />
        </div>

        <div className="absolute inset-0 grid-pattern opacity-20" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-teal-glow/30 bg-teal-glow/10 backdrop-blur-sm text-teal-glow text-sm font-medium mb-8 animate-hero-fade-in"
            >
              <Sparkles className="h-4 w-4" />
              {t("home.heroBadge")}
            </div>

            <h1
              className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 leading-[1.05] text-white animate-hero-fade-up text-balance"
            >
              {t("home.heroTitle1")}{" "}
              <span className="text-gradient block">{t("home.heroTitle2")}</span>
              {t("home.heroTitle3")}
            </h1>


            <p
              className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-12 leading-relaxed animate-hero-fade-up [animation-delay:200ms]"
            >
              {t("home.heroSubtitle")}
            </p>

            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-hero-fade-up [animation-delay:350ms]"
            >
              <Button asChild size="lg" className="text-base px-10 py-7 font-semibold shadow-glow-lg rounded-xl bg-primary hover:bg-primary/90">
                <Link to="/book-demo">
                  {t("cta.bookDemo")} <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-base px-10 py-7 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white backdrop-blur-sm rounded-xl">
                <Link to="/yacht-platform-features">{t("cta.explorePlatform")}</Link>
              </Button>
            </div>
          </div>
        </div>

        
      </section>

      {/* Feature Marquee — LIGHT */}
      <section className="relative border-b border-border/50 bg-background overflow-hidden py-6 sm:py-8 md:py-14">
        <div className="absolute inset-0 grid-pattern-light opacity-40" />
        <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-24 md:w-40 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-24 md:w-40 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        <div className="relative flex items-center">
          <div className="animate-marquee-reverse flex whitespace-nowrap">
            {[...Array(4)].map((_, setIdx) => (
              <div key={setIdx} className="flex shrink-0 items-center">
                {[
                  { label: t("home.marqueeCharter"), to: "/yacht-industry-solutions/charter-broker-software" },
                  { label: t("home.marqueeYachtBrokers"), to: "/yacht-industry-solutions/yacht-broker-software" },
                  { label: t("home.marqueeYachtManagers"), to: "/yacht-industry-solutions/yacht-manager-software" },
                ].map((item, i) => (
                  <span key={`${setIdx}-${i}`} className="flex items-center">
                    <Link
                      to={item.to}
                      className="font-display text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-extrabold uppercase tracking-tight text-foreground/[0.07] hover:text-foreground/[0.15] transition-colors duration-300 select-none"
                    >
                      {item.label}
                    </Link>
                    <span className="mx-3 sm:mx-6 md:mx-10 text-foreground/[0.12] text-lg sm:text-2xl md:text-3xl select-none">◆</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <LazyMotion features={domAnimation} strict>
        {/* Global Coverage — DARK */}
        <section className="relative py-24 overflow-hidden section-dark">
          <div className="absolute inset-0 bg-gradient-premium-dark" />
          <div className="absolute inset-0 grid-pattern opacity-10" />
          <div className="container mx-auto px-4 relative">
            <m.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}
              className="text-center mb-4"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-teal-glow/30 bg-teal-glow/10 text-teal-glow text-xs font-semibold uppercase tracking-wider mb-6">
                <Waves className="h-3.5 w-3.5" />
                {t("home.globalBadge")}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4 text-white">
                {t("home.globalTitle1")} <span className="text-gradient">{t("home.globalTitle2")}</span> {t("home.globalTitle3")}
              </h2>
              <p className="text-white/50 text-lg max-w-2xl mx-auto">
                {t("home.globalSubtitle")}
              </p>
            </m.div>

            <m.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden border border-white/10"
            >
              <img
                src={yachtGlobal}
                alt={t("alt.globalCoverage")}
                className="w-full h-[400px] md:h-[500px] object-cover"
                loading="lazy"
                decoding="async"
                width={1200}
                height={500}
              />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
            </m.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 max-w-4xl mx-auto">
              {[
                { region: t("home.regionMed"), count: "30+" },
                { region: t("home.regionCarib"), count: "10+" },
                { region: t("home.regionAsia"), count: "10+" },
                { region: t("home.regionGulf"), count: "20+" },
              ].map((r, i) => (
                <m.div
                  key={r.region}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}
                  className="text-center p-4 rounded-xl border border-white/10 glass-card-dark"
                >
                  <div className="font-display text-2xl font-bold text-teal-glow mb-1">{r.count}</div>
                  <div className="text-sm text-white/50">{r.region}</div>
                </m.div>
              ))}
            </div>
          </div>
        </section>

        {/* Products — LIGHT */}
        <section className="py-24 relative bg-gradient-premium">
          <div className="absolute inset-0 grid-pattern-light opacity-30" />
          <div className="container mx-auto px-4 relative">
            <m.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-6">
                <Zap className="h-3.5 w-3.5" />
                {t("home.productsBadge")}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4 text-foreground">
                {t("home.productsTitle1")} <span className="text-gradient">{t("home.productsTitle2")}</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {t("home.productsSubtitle")}
              </p>
            </m.div>

            <m.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden border border-border/30 mb-12"
            >
              <img
                 srcSet={`${yachtAerialMobile} 480w, ${yachtAerial} 900w`}
                 sizes="(max-width: 768px) 100vw, 900px"
                src={yachtAerial}
                alt={t("alt.productsAerial")}
                className="w-full h-[280px] md:h-[360px] object-cover"
                loading="lazy"
                decoding="async"
                width={1280}
                height={360}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
            </m.div>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {products.map((p, i) => (
                <m.div
                  key={p.title}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                  variants={fadeUp}
                  className="h-full"
                >
                  <Link
                    to={p.href}
                    className={`group flex flex-col h-full p-8 rounded-2xl border ${p.border} bg-white/70 backdrop-blur-sm hover:shadow-glow transition-all duration-500`}
                  >
                    <div className={`h-14 w-14 rounded-xl ${p.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <p.icon className={`h-7 w-7 ${p.color}`} />
                    </div>
                    <h3 className="font-display font-bold text-xl mb-3 text-foreground group-hover:text-primary transition-colors">{p.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-grow mb-4">{p.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{p.modules}</span>
                      <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                </m.div>
              ))}
            </div>

            <m.div
              initial="hidden" whileInView="visible" viewport={{ once: true }} custom={4} variants={fadeUp}
              className="text-center mt-10"
            >
              <Button asChild variant="outline" className="border-primary/30 text-primary hover:bg-primary/5">
                <Link to="/yacht-platform-features">
                  {t("cta.explorePlatform")} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </m.div>
          </div>
        </section>
      </LazyMotion>

      <Suspense fallback={null}>
        <HomeBelowFold yachtBridge={yachtBridge} yachtDeck={yachtDeck} />
      </Suspense>
    </Layout>
  );
};

export default Index;
